import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Save, Zap, ExternalLink } from "lucide-react";

const initial = {
  shopify_store_domain: "",
  shopify_client_id: "",
  shopify_client_secret: "",
  shopify_admin_token: "",
  pricing_display: "Retail + Wholesale",
  customer_domain: "",
  limit_display: "Both",
  email_notifications: true,
  appearance_color: "#1a2340",
};

export default function Settings() {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get("/settings").then((r) => setForm({ ...initial, ...r.data }));
    api.get("/auth/status").then((r) => setOauthStatus(r.data)).catch(() => {});
    if (searchParams.get("installed") === "1") {
      toast.success(`Shopify app installed for ${searchParams.get("shop") || "your store"}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

function startOAuthInstall() {
  console.log("OAuth button clicked");

  const shop = (form.shopify_store_domain || "")
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

  if (!shop || !shop.endsWith(".myshopify.com")) {
    toast.error("Enter a valid store domain like your-store.myshopify.com");
    return;
  }

  // Save settings, then redirect to Shopify
  api.put("/settings", form).finally(() => {
    window.location.href =
   `${API}/auth/install?shop=${encodeURIComponent(shop)}`  
  });
} 

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      // Preserve strategy: send full form (backend upserts). Blank secret leaves field blank.
      const r = await api.put("/settings", payload);
      setForm({ ...initial, ...r.data });
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      await api.put("/settings", form); // save latest before test
      const r = await api.post("/settings/test-shopify");
      setTestResult(r.data);
      if (r.data.connected) toast.success(`Connected to ${r.data.shop_name}`);
      else toast.error(r.data.message);
    } catch (e) {
      setTestResult({ connected: false, message: "Test failed" });
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  }

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="App Settings"
        subtitle="Connect Shopify and customize display, notifications, and appearance"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving} data-testid="settings-save">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <div className="space-y-6">
        {/* Shopify Connection */}
        <div className="b2b-card p-6" data-testid="settings-shopify-card">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-semibold" style={{ color: "var(--brand-navy)" }}>Shopify Connection</h3>
              <p className="text-sm mt-1" style={{ color: "var(--brand-muted)" }}>
                Install via OAuth (recommended) or paste an existing Admin API token below.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-primary inline-flex items-center gap-2" onClick={startOAuthInstall} data-testid="settings-oauth-install">
                <ExternalLink size={14} /> Install / Reconnect via Shopify
              </button>
              <button className="btn-secondary inline-flex items-center gap-2" onClick={testConnection} disabled={testing} data-testid="settings-test-connection">
                <Zap size={14} /> {testing ? "Testing…" : "Test connection"}
              </button>
            </div>
          </div>

          {oauthStatus && (
            <div
              className="mb-4 px-4 py-3 rounded-lg text-sm flex items-start gap-2"
              style={{
                background: oauthStatus.connected ? "#e6ecf7" : "#fff4c2",
                color: oauthStatus.connected ? "#1a2340" : "#8a6c00",
              }}
              data-testid="settings-oauth-status"
            >
              {oauthStatus.connected ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              <span>
                {oauthStatus.connected
                  ? `OAuth installed for ${oauthStatus.shop} · scopes: ${oauthStatus.scope || "n/a"}`
                  : `OAuth not installed. ${oauthStatus.message || ""}`}
              </span>
            </div>
          )}

          {testResult && (
            <div
              className="mb-4 px-4 py-3 rounded-lg text-sm flex items-start gap-2"
              style={{
                background: testResult.connected ? "#cdf5e1" : "#ffd6d1",
                color: testResult.connected ? "#005c46" : "#9b1300",
              }}
              data-testid="settings-test-result"
            >
              {testResult.connected ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              <span>
                {testResult.connected
                  ? `Connected to ${testResult.shop_name} (${testResult.email})`
                  : testResult.message}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Shopify store domain</label>
              <input className="form-input" placeholder="my-store.myshopify.com" value={form.shopify_store_domain} onChange={set("shopify_store_domain")} data-testid="settings-store-domain" />
            </div>
            <div>
              <label className="form-label">Admin API access token</label>
              <input className="form-input" placeholder="shpat_xxxx" value={form.shopify_admin_token} onChange={set("shopify_admin_token")} data-testid="settings-admin-token" />
              <p className="text-xs mt-1" style={{ color: "var(--brand-muted)" }}>
                Create a custom app in your Shopify admin → Apps → Develop apps, then install and copy the token.
              </p>
            </div>
            <div>
              <label className="form-label">Client ID (API key)</label>
              <input className="form-input" value={form.shopify_client_id} onChange={set("shopify_client_id")} data-testid="settings-client-id" />
            </div>
            <div>
              <label className="form-label">Client Secret</label>
              <input type="password" className="form-input" value={form.shopify_client_secret} onChange={set("shopify_client_secret")} data-testid="settings-client-secret" />
            </div>
          </div>
        </div>

        {/* Pricing display */}
        <div className="b2b-card p-6" data-testid="settings-pricing-card">
          <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>Pricing display</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">How to display wholesale prices</label>
              <select className="form-select" value={form.pricing_display} onChange={set("pricing_display")} data-testid="settings-pricing-display">
                <option>Retail + Wholesale</option>
                <option>Wholesale Only</option>
                <option>Percentage Off</option>
              </select>
            </div>
            <div>
              <label className="form-label">Appearance color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.appearance_color} onChange={set("appearance_color")} className="w-12 h-10 rounded border" data-testid="settings-appearance-color" />
                <input type="text" className="form-input flex-1" value={form.appearance_color} onChange={set("appearance_color")} />
              </div>
            </div>
          </div>
        </div>

        {/* Customer domain */}
        <div className="b2b-card p-6" data-testid="settings-domain-card">
          <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>Customer domain</h3>
          <label className="form-label">Wholesale customer portal domain</label>
          <input className="form-input" placeholder="wholesale.mystore.com" value={form.customer_domain} onChange={set("customer_domain")} data-testid="settings-customer-domain" />
        </div>

        {/* Limit display */}
        <div className="b2b-card p-6" data-testid="settings-limits-card">
          <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>Limit display</h3>
          <label className="form-label">Where to show order/product limit messaging</label>
          <select className="form-select" value={form.limit_display} onChange={set("limit_display")} data-testid="settings-limit-display">
            <option>Show on Product Page</option>
            <option>Show at Checkout</option>
            <option>Both</option>
          </select>
        </div>

        {/* Notifications */}
        <div className="b2b-card p-6" data-testid="settings-notify-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold" style={{ color: "var(--brand-navy)" }}>Email customer notifications</h3>
              <p className="text-sm mt-1" style={{ color: "var(--brand-muted)" }}>
                Notify customers when order or product limits are hit.
              </p>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.email_notifications} onChange={set("email_notifications")} data-testid="settings-notify-toggle" />
              <span className="slider" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
